export const fetchData = async (
  carsInput, // cars service, or an object containing all dependencies
  rentalsInput,
  couponsInput,
  contactInput,
  driversInput,
  toursInput,
  tourBookingsInput,
  employeeServiceInput,
  setCarsDataInput,
  setRentalsDataInput,
  setCouponsDataInput,
  setTransfersDataInput,
  setDriversDataInput,
  setToursDataInput,
  setTourBookingsDataInput,
  setEmployeesDataInput,
  setSeasonalPricingInput,
  setIsLoadingInput,
  setErrorInput
) => {
  let cars, rentals, coupons, contact, drivers, tours, tourBookings, employeeService;
  let setCarsData, setRentalsData, setCouponsData, setTransfersData, setDriversData,
      setToursData, setTourBookingsData, setEmployeesData, setSeasonalPricing,
      setIsLoading, setError;

  if (typeof carsInput === 'object' && carsInput !== null &&
      carsInput.cars && typeof carsInput.cars.getAll === 'function' &&
      typeof carsInput.setError === 'function' &&
      setErrorInput === undefined) {

    ({
      cars, rentals, coupons, contact, drivers, tours, tourBookings, employeeService,
      setCarsData, setRentalsData, setCouponsData, setTransfersData, setDriversData,
      setToursData, setTourBookingsData, setEmployeesData, setSeasonalPricing,
      setIsLoading, setError
    } = carsInput);
  } else {
    cars = carsInput;
    rentals = rentalsInput;
    coupons = couponsInput;
    contact = contactInput;
    drivers = driversInput;
    tours = toursInput;
    tourBookings = tourBookingsInput;
    employeeService = employeeServiceInput;
    setCarsData = setCarsDataInput;
    setRentalsData = setRentalsDataInput;
    setCouponsData = setCouponsDataInput;
    setTransfersData = setTransfersDataInput;
    setDriversData = setDriversDataInput;
    setToursData = setToursDataInput;
    setTourBookingsData = setTourBookingsDataInput;
    setEmployeesData = setEmployeesDataInput;
    setSeasonalPricing = setSeasonalPricingInput;
    setIsLoading = setIsLoadingInput;
    setError = setErrorInput;
  }
  // Check which dependencies are valid
  const validDependencies = {
    carsValid: !!(cars && typeof cars.getAll === 'function'),
    rentalsValid: !!(rentals && typeof rentals.getAll === 'function'),
    couponsValid: !!(coupons && typeof coupons.getAll === 'function'),
    contactValid: !!(contact && typeof contact.getAllTransfers === 'function'),
    driversValid: !!(drivers && typeof drivers.getAllDrivers === 'function'),
    toursValid: !!(tours && typeof tours.getAll === 'function'),
    tourBookingsValid: !!(tourBookings && typeof tourBookings.getAll === 'function'),
    employeeServiceValid: !!(employeeService && typeof employeeService.getAllEmployees === 'function'),
    setErrorValid: !!(setError && typeof setError === 'function'),
    setIsLoadingValid: !!(setIsLoading && typeof setIsLoading === 'function'),
  };

  // Check if critical setters are missing
  if (typeof setIsLoading !== 'function' || typeof setError !== 'function') {
    console.error("fetchData: Critical setters (setIsLoading, setError) are missing. Cannot proceed safely.");
    return;
  }

  // Log which dependencies are invalid but continue with valid ones
  const invalidDependencies = Object.entries(validDependencies)
    .filter(([key, valid]) => !valid)
    .map(([key]) => key);

  if (invalidDependencies.length > 0) {
    console.warn("fetchData: Some dependencies are invalid, but continuing with valid ones:", invalidDependencies);
  }
  try {
    setIsLoading(true); // Set loading state

    // Create array of promises only for valid services
    const promises = [];
    const promiseMap = {};

    if (validDependencies.carsValid) {
      promises.push(cars.getAll());
      promiseMap['cars'] = promises.length - 1;
    }
    if (validDependencies.rentalsValid) {
      promises.push(rentals.getAll());
      promiseMap['rentals'] = promises.length - 1;
    }
    if (validDependencies.couponsValid) {
      promises.push(coupons.getAll());
      promiseMap['coupons'] = promises.length - 1;
    }
    if (validDependencies.contactValid) {
      promises.push(contact.getAllTransfers());
      promiseMap['transfers'] = promises.length - 1;
    }
    if (validDependencies.driversValid) {
      promises.push(drivers.getAllDrivers());
      promiseMap['drivers'] = promises.length - 1;
    }
    if (validDependencies.toursValid) {
      promises.push(tours.getAll());
      promiseMap['tours'] = promises.length - 1;
    }
    if (validDependencies.tourBookingsValid) {
      promises.push(tourBookings.getAll());
      promiseMap['tourBookings'] = promises.length - 1;
    }
    if (validDependencies.employeeServiceValid) {
      promises.push(employeeService.getAllEmployees());
      promiseMap['employees'] = promises.length - 1;
    }

    const results = await Promise.all(promises);

    // Process seasonal pricing data
    const seasonalPricingData = {};
    if (promiseMap.hasOwnProperty('cars')) {
      const carsRes = results[promiseMap['cars']];
      // Add null check for carsRes and carsRes.data
      if (carsRes && carsRes.data && Array.isArray(carsRes.data)) {
        carsRes.data.forEach((car) => {
          if (car.availability && car.availability.length > 0) {
            seasonalPricingData[car._id] = car.availability;
          }
        });
      } else if (carsRes && (!carsRes.data || !Array.isArray(carsRes.data))) {
        console.warn("Cars data from API is missing or not an array:", carsRes);
      }
    }

    // Set data only for services that were successfully fetched
    if (promiseMap.hasOwnProperty('cars') && typeof setCarsData === 'function') {
      setCarsData(results[promiseMap['cars']].data);
    }    if (promiseMap.hasOwnProperty('rentals') && typeof setRentalsData === 'function') {
      const rentalsResult = results[promiseMap['rentals']];
      // Handle new pagination response format
      if (rentalsResult.data && rentalsResult.data.rentals) {
        setRentalsData(rentalsResult.data.rentals);
      } else if (Array.isArray(rentalsResult.data)) {
        // Fallback for old response format
        setRentalsData(rentalsResult.data);
      } else {
        setRentalsData([]);
      }
    }
    if (promiseMap.hasOwnProperty('coupons') && typeof setCouponsData === 'function') {
      setCouponsData(results[promiseMap['coupons']].data);
    }
    if (promiseMap.hasOwnProperty('transfers') && typeof setTransfersData === 'function') {
      setTransfersData(results[promiseMap['transfers']].data);
    }
    if (promiseMap.hasOwnProperty('drivers') && typeof setDriversData === 'function') {
      setDriversData(results[promiseMap['drivers']].data);
    }
    if (promiseMap.hasOwnProperty('tours') && typeof setToursData === 'function') {
      setToursData(results[promiseMap['tours']].data);
    }
    if (promiseMap.hasOwnProperty('tourBookings') && typeof setTourBookingsData === 'function') {
      setTourBookingsData(results[promiseMap['tourBookings']].data);
    }
    if (promiseMap.hasOwnProperty('employees') && typeof setEmployeesData === 'function') {
      setEmployeesData(results[promiseMap['employees']].data);
    }
    if (typeof setSeasonalPricing === 'function') {
      setSeasonalPricing(seasonalPricingData);
    }
    
    setIsLoading(false);
  } catch (error) {
    console.error("Error fetching data:", error);
    setError("Failed to load some dashboard data");
    setIsLoading(false);
  }
};
